import React, { useEffect, useRef, useState } from "react";
import apiClient from "../../../actions/api";
import isEmpty from "../../../validations/is-empty";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  address: "",
  linkedin: "",
  portfolio: "",
  referral: "",
  discoverySource: "",
  discoverySourceOther: "",
};

const MAX_INTRODUCTION_SIZE = 50 * 1024 * 1024;

const getRecordingMimeType = () => {
  const supportedTypes = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];

  return supportedTypes.find((type) => MediaRecorder.isTypeSupported(type));
};

// Add or remove discovery sources here. "Other" is appended automatically.
const DISCOVERY_SOURCES = [
  { value: "Indeed", label: "Indeed" },
  { value: "Dice", label: "Dice" },
  { value: "LinkedIn", label: "LinkedIn" },
  { value: "Careers", label: "Careers" },
  { value: "Builtin", label: "Builtin" },
];

const DetailSection = ({ job }) => {
  const [responsibilities, setResponsibilities] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [offerdetails, setOfferdetails] = useState([]);
  const [jobDescription, setJobDescription] = useState([]);

  const [form, setForm] = useState(initialForm);
  const [resume, setResume] = useState(null);
  const [introduction, setIntroduction] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingPreview, setRecordingPreview] = useState("");

  const resumeInputRef = useRef(null);
  const introductionInputRef = useRef(null);
  const liveVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordedChunksRef = useRef([]);

  useEffect(() => {
    if (!isEmpty(job)) {
      setResponsibilities(job?.responsibilities?.split("\n"));
      setRequirements(job?.requirements?.split("\n"));
      setOfferdetails(job?.offerDetails?.split("\n"));
      setJobDescription(job?.description?.split("\n"));
    }
  }, [job]);

  useEffect(
    () => () => {
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
      if (recordingPreview) URL.revokeObjectURL(recordingPreview);
    },
    [recordingPreview],
  );

  useEffect(() => {
    if (isRecording && liveVideoRef.current && mediaStreamRef.current) {
      liveVideoRef.current.srcObject = mediaStreamRef.current;
    }
  }, [isRecording]);

  const updateField = ({ target: { name, value } }) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email))
      next.email = "Please enter a valid email address.";
    if (!form.phone.trim()) next.phone = "Please enter your phone number.";
    if (!form.address.trim())
      next.address = "Please enter your current address.";
    if (!form.linkedin.trim())
      next.linkedin = "Please enter your LinkedIn profile URL.";
    if (!resume) next.resume = "Please upload your resume or CV.";
    if (resume && resume.size > 10 * 1024 * 1024)
      next.resume = "Resume must be 10 MB or smaller.";
    if (!form.discoverySource)
      next.discoverySource = "Please select where you found us.";
    if (form.discoverySource === "Other" && !form.discoverySourceOther.trim())
      next.discoverySourceOther = "Please enter where you found us.";
    if (!introduction)
      next.introduction = "Please record or upload an introduction video.";
    if (introduction && !introduction.type.startsWith("video/"))
      next.introduction = "Introduction must be a video file.";
    if (introduction && introduction.size >= MAX_INTRODUCTION_SIZE)
      next.introduction = "Introduction video must be smaller than 50 MB.";
    return next;
  };

  const setIntroductionVideo = (file) => {
    setIntroduction(file || null);
    setErrors((current) => ({ ...current, introduction: undefined }));
    setRecordingPreview((current) => {
      if (current) URL.revokeObjectURL(current);
      return file ? URL.createObjectURL(file) : "";
    });
  };

  const stopMediaStream = () => {
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    if (liveVideoRef.current) liveVideoRef.current.srcObject = null;
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        throw new Error("Video recording is not supported by this browser.");
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      const recordingMimeType = getRecordingMimeType();
      const recorder = new MediaRecorder(
        stream,
        recordingMimeType ? { mimeType: recordingMimeType } : undefined,
      );

      recordedChunksRef.current = [];
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = ({ data }) => {
        if (data.size) recordedChunksRef.current.push(data);
      };
      recorder.onstop = () => {
        // Remove codec parameters because multipart parsers and backend file
        // filters expect a standard MIME type such as video/webm.
        const type = (recorder.mimeType || recordingMimeType || "video/webm")
          .split(";")[0]
          .toLowerCase();
        const normalizedType = type === "video/mp4" ? "video/mp4" : "video/webm";
        const blob = new Blob(recordedChunksRef.current, {
          type: normalizedType,
        });
        const extension = normalizedType === "video/mp4" ? "mp4" : "webm";
        setIntroductionVideo(
          new File([blob], `introduction-${Date.now()}.${extension}`, {
            type: normalizedType,
          }),
        );
        stopMediaStream();
      };

      recorder.start();
      setIsRecording(true);
      setErrors((current) => ({ ...current, introduction: undefined }));
    } catch (error) {
      stopMediaStream();
      setErrors((current) => ({
        ...current,
        introduction:
          "Camera and microphone access is required to record a video.",
      }));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validate();

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setStatus({
        type: "error",
        message: "Please correct the highlighted fields.",
      });
      return;
    }

    const data = new FormData();

    Object.entries(form).forEach(([key, value]) =>
      data.append(key, value.trim()),
    );

    data.append("resume", resume);

    if (introduction) {
      data.append("introduction", introduction);
    }

    if (job?._id) {
      data.append("jobId", job._id);
    }

    if (job?.slug) {
      data.append("jobSlug", job.slug);
    }

    setSubmitting(true);
    setStatus(null);

    apiClient
      .post("/candidates/add-candidate", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        if (response.data?.status) {
          setStatus({
            type: "success",
            message:
              response.data?.message ||
              "Your application has been submitted successfully.",
          });

          setForm(initialForm);
          setResume(null);
          setIntroduction(null);
          setRecordingPreview((current) => {
            if (current) URL.revokeObjectURL(current);
            return "";
          });
          setErrors({});

          if (resumeInputRef.current) {
            resumeInputRef.current.value = "";
          }

          if (introductionInputRef.current) {
            introductionInputRef.current.value = "";
          }

          event.currentTarget.reset();
        }
      })
      .catch((error) => {
        const apiErrors = error.response?.data?.errors;

        if (Array.isArray(apiErrors)) {
          const fieldErrors = {};

          apiErrors.forEach(({ path, param, msg }) => {
            if (path || param) {
              fieldErrors[path || param] = msg;
            }
          });

          setErrors(fieldErrors);

          setStatus({
            type: "error",
            message:
              error.response?.data?.message ||
              apiErrors?.[0]?.msg ||
              "Unable to submit your application. Please try again.",
          });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const fieldError = (name) =>
    errors[name] && <small className="field-error">{errors[name]}</small>;

  return (
    <section className="page-section page-section-tight">
      <div className="container job-detail-grid">
        <article className="job-detail-content">
          <div className="badge-row">
            {job?.badges?.map((item, key) => {
              return (
                <span className="badge badge-teal" key={key}>
                  {item}
                </span>
              );
            })}
          </div>
          <div className="content-block first">
            <h2>Job Description</h2>
            <p>
              {jobDescription.map((item, key) => {
                return <div key={key}>{item}</div>;
              })}
            </p>
          </div>
          <div className="content-block">
            <h2>Key Responsibilities</h2>
            <ul className="detail-list">
              {responsibilities.map((item, key) => {
                return <li key={key}>{item}</li>;
              })}
            </ul>
          </div>
          <div className="content-block">
            <h2>Requirements / Qualifications</h2>
            <ul className="detail-list">
              {requirements.map((item, key) => {
                return <li key={key}>{item}</li>;
              })}
            </ul>
          </div>
          <div className="content-block">
            <h2>What We Offer</h2>
            <ul className="detail-list">
              {offerdetails.map((item, key) => {
                return <li key={key}>{item}</li>;
              })}
            </ul>
          </div>
        </article>
        <aside className="sticky-card-stack">
          <section className="card sticky-card">
            <h2>Application Checklist</h2>
            <p>Prepare these items before submitting your application.</p>
            <ul className="detail-list compact">
              <li>Resume or CV in PDF, DOC, or DOCX format</li>
              <li>
                LinkedIn profile (required) and portfolio link, if available
              </li>
              <li>Referral or outreacher name</li>
              <li>Introduction video (recorded or uploaded, under 50 MB)</li>
            </ul>
            <a
              className="button button-primary button-md full-width"
              href="#application"
            >
              Apply Now
            </a>
          </section>
          <form
            className="card application-form-card"
            id="application"
            noValidate
            onSubmit={handleSubmit}
          >
            <p className="eyebrow">Application</p>
            <h2>Apply for {job?.title || "this position"}</h2>
            <p>
              Please record or upload a short introduction video in English.
              Explain your background, experience, and why you are interested in
              this position.
            </p>
            {status && (
              <div className={`form-alert ${status.type}`} role="alert">
                {status.message}
              </div>
            )}
            <div className="job-form-grid">
              <label className="field">
                <span className="field-label">
                  Full Name
                  <span className="required-marker" aria-hidden="true">
                    *
                  </span>
                </span>
                <input
                  name="name"
                  required
                  value={form.name}
                  onChange={updateField}
                />
                {fieldError("name")}
              </label>
              <label className="field">
                <span className="field-label">
                  Email Address
                  <span className="required-marker" aria-hidden="true">
                    *
                  </span>
                </span>
                <input
                  name="email"
                  required
                  type="email"
                  value={form.email}
                  onChange={updateField}
                />
                {fieldError("email")}
              </label>
              <label className="field">
                <span className="field-label">
                  Phone Number
                  <span className="required-marker" aria-hidden="true">
                    *
                  </span>
                </span>
                <input
                  name="phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={updateField}
                />
                {fieldError("phone")}
              </label>
              <label className="field">
                <span className="field-label">
                  Address
                  <span className="required-marker" aria-hidden="true">
                    *
                  </span>
                </span>
                <input
                  name="address"
                  required
                  value={form.address}
                  onChange={updateField}
                />
                {fieldError("address")}
              </label>
              <label className="field">
                <span className="field-label">
                  LinkedIn Profile
                  <span className="required-marker" aria-hidden="true">
                    *
                  </span>
                </span>
                <input
                  placeholder="https://linkedin.com/in/..."
                  type="url"
                  name="linkedin"
                  required
                  value={form.linkedin}
                  onChange={updateField}
                />
                {fieldError("linkedin")}
              </label>
              <label className="field">
                <span className="field-label">Portfolio / Website</span>
                <input
                  placeholder="https://..."
                  type="url"
                  name="portfolio"
                  value={form.portfolio}
                  onChange={updateField}
                />
              </label>
            </div>
            <label className="field">
              <span className="field-label">
                Resume / CV
                <span className="required-marker" aria-hidden="true">
                  *
                </span>
              </span>
              <input
                accept=".pdf,.doc,.docx"
                type="file"
                ref={resumeInputRef}
                onChange={(event) => {
                  setResume(event.target.files[0] || null);
                  setErrors((current) => ({ ...current, resume: undefined }));
                }}
              />
              <small className="file-summary neutral">
                Resume capacity available: up to 10.0 MB.
              </small>
              <small className="field-hint">
                PDF, DOC, or DOCX. Max 10 MB.
              </small>
              {fieldError("resume")}
            </label>
            <label className="field">
              <span className="field-label">
                Introduction Video
                <span className="required-marker" aria-hidden="true">
                  *
                </span>
              </span>
              <input
                accept="video/*"
                type="file"
                ref={introductionInputRef}
                onChange={(event) =>
                  setIntroductionVideo(event.target.files[0] || null)
                }
              />
              <small className="field-hint">
                Upload a video smaller than 50 MB, or record one below.
              </small>
              {fieldError("introduction")}
            </label>
            <div className="recording-panel">
              <div>
                <strong>Record your introduction</strong>
                <p className="field-hint">
                  Your browser will ask for camera and microphone access.
                </p>
              </div>
              {isRecording && (
                <video
                  ref={liveVideoRef}
                  className="media-preview live-recording-preview"
                  autoPlay
                  muted
                  playsInline
                />
              )}
              {recordingPreview && !isRecording && (
                <video
                  className="media-preview"
                  src={recordingPreview}
                  controls
                />
              )}
              <div className="recording-actions">
                {!isRecording ? (
                  <button
                    className="button button-secondary button-md"
                    type="button"
                    onClick={startRecording}
                  >
                    Record Video
                  </button>
                ) : (
                  <button
                    className="button button-secondary button-md"
                    type="button"
                    onClick={stopRecording}
                  >
                    Stop Recording
                  </button>
                )}
                {introduction && !isRecording && (
                  <span className="recording-status">
                    Video ready ({(introduction.size / 1024 / 1024).toFixed(1)}{" "}
                    MB)
                  </span>
                )}
              </div>
            </div>
            <div className="job-form-grid">
              <label className="field">
                <span className="field-label">
                  Where did you find us?
                  <span className="required-marker" aria-hidden="true">
                    *
                  </span>
                </span>
                <select
                  name="discoverySource"
                  required
                  value={form.discoverySource}
                  onChange={(event) => {
                    updateField(event);
                    if (event.target.value !== "Other") {
                      setForm((current) => ({
                        ...current,
                        discoverySourceOther: "",
                      }));
                    }
                  }}
                >
                  <option value="">Select an option</option>
                  {DISCOVERY_SOURCES.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                  <option value="Other">Other</option>
                </select>
                {fieldError("discoverySource")}
              </label>
              {form.discoverySource === "Other" && (
                <label className="field">
                  <span className="field-label">
                    Please specify
                    <span className="required-marker" aria-hidden="true">
                      *
                    </span>
                  </span>
                  <input
                    name="discoverySourceOther"
                    required
                    value={form.discoverySourceOther}
                    onChange={updateField}
                  />
                  {fieldError("discoverySourceOther")}
                </label>
              )}
            </div>
            <div className="job-form-grid">
              <label className="field">
                <span className="field-label">Referral / Outreacher</span>
                <select
                  name="referral"
                  required
                  value={form.referral}
                  onChange={updateField}
                >
                  <option value="">Select referral</option>
                  <option value="recruiter">Recruiter</option>
                  <option value="employeer">Employeer</option>
                </select>
              </label>
            </div>

            <button
              className="button button-primary button-md full-width"
              style={{ marginTop: 20 }}
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
          </form>
        </aside>
      </div>
    </section>
  );
};

export default DetailSection;
